import type { Industry } from '../types/account.js';
import type { PortalLibrarySection } from '../types/portalLibrary.js';

// CLIENT PORTAL — WHICH STRATEGY SECTIONS A VIEWER HAS (Round 6 §3). PURE.
//
// Scott: franchise clients keep "Monthly campaigns" + "Alternate campaigns";
// Auto and everyone else get ONE section instead, "Campaigns & promotions".
//
// ⚠️⚠️⚠️ `automotive_franchise` IS NOT ON THE "FRANCHISE" SIDE. The Industry
// type is `fitness_franchise | automotive_franchise | small_business_ecommerce |
// other`, and Scott's "franchise clients" means FITNESS franchise only. The
// automotive value contains the word "franchise" and belongs with AUTO — the
// other side of this split. Reading the type name gets it exactly backwards, and
// the symptom is a dealership being shown "Monthly campaigns". So the franchise
// side is an explicit LIST OF VALUES, never a test on the string: no
// `includes('franchise')`, no prefix, no suffix. strategyNav.test.ts pins the
// automotive case by name and asserts this file names no such test.
//
// ⚠️⚠️ ONE ANSWER, USED THREE WAYS, so the three cannot disagree:
//   1. `/api/portal/me` sends the viewer's resolved value (never per-account
//      industries) and the NAV is built from it;
//   2. the library's visibility check refuses a strategy section outside the
//      viewer's nav, so a URL cannot reach what the nav does not offer;
//   3. the publish form's reach counts how many matched accounts have the chosen
//      section, and a zero is refused.

export type PortalStrategyNav = 'franchise' | 'campaigns';

/**
 * The industries that get the franchise strategy sections. ⚠️ A LIST OF VALUES,
 * see the header. `automotive_franchise` is deliberately absent.
 */
export const FRANCHISE_STRATEGY_INDUSTRIES: readonly Industry[] = ['fitness_franchise'];

/**
 * The viewer's strategy nav, from the industries of the accounts in their scope.
 *
 * ⚠️⚠️ THE MIXED-SCOPE RULE, stated because it is arbitrary and someone will hit
 * it: ANY fitness-franchise account in scope resolves to `franchise`. A
 * franchisee group that also owns a dealership should not lose the sections its
 * franchise business uses — losing Monthly campaigns because one of your
 * businesses is a car yard would read as us having taken something away. The
 * cost runs the other way and is smaller: that group sees no "Campaigns &
 * promotions" section.
 *
 * An empty scope, or one with no industry recorded, is `campaigns` — the
 * everyone-else answer.
 */
export function resolveStrategyNav(
  industries: Iterable<Industry | null | undefined>,
): PortalStrategyNav {
  for (const industry of industries) {
    if (industry && FRANCHISE_STRATEGY_INDUSTRIES.includes(industry)) return 'franchise';
  }
  return 'campaigns';
}

/**
 * Is this a strategy nav value we know? ⚠️ For the browser's `/me` read: an
 * unknown value renders NO nav (as if still loading) rather than being guessed
 * into one shape — guessing is the per-viewer decision moving into the client.
 */
export function isPortalStrategyNav(v: unknown): v is PortalStrategyNav {
  return v === 'franchise' || v === 'campaigns';
}

/** The strategy sections each nav shape carries, in nav order. */
export const STRATEGY_SECTIONS: Record<PortalStrategyNav, readonly PortalLibrarySection[]> = {
  franchise: ['strategy.monthly', 'strategy.alternate'],
  campaigns: ['strategy.campaigns'],
};

const ALL_STRATEGY_SECTIONS: ReadonlySet<PortalLibrarySection> = new Set(
  Object.values(STRATEGY_SECTIONS).flat(),
);

/**
 * Whether a library section exists for a viewer with this strategy nav.
 *
 * ⚠️ Only the STRATEGY sections depend on it. Kyck college and custom reports
 * are the same for every viewer — custom reports' "only when you have one" is a
 * CONTENT rule, decided by the viewer's tiles, not by this.
 */
export function isSectionInStrategyNav(
  section: PortalLibrarySection,
  nav: PortalStrategyNav,
): boolean {
  if (!ALL_STRATEGY_SECTIONS.has(section)) return true;
  return STRATEGY_SECTIONS[nav].includes(section);
}

/** Who a strategy section is for, in words — for the publish form's refusal. */
export function strategySectionAudienceLabel(section: PortalLibrarySection): string | null {
  if (STRATEGY_SECTIONS.franchise.includes(section)) return 'fitness franchise clients';
  if (STRATEGY_SECTIONS.campaigns.includes(section)) {
    return 'clients who are not fitness franchises (automotive and everyone else)';
  }
  return null;
}
